/* eslint-disable ember/classic-decorator-hooks */
import EmberObject, { set } from '@ember/object';

import * as CircuitUtils from '../utils/circuit';
import * as CommonUtils from '../../utils/common';
import * as ResistorToleranceUtils from '../tolerances/resistor-tolerance-utils';
import { computeNominalValue } from '../tolerances/tolerance-utils';

//
// The idea behind a these components (capacitor, resistor, inductor) is we have classes that encapsulate
// how each of them are created. This class is used for creating a resistor.
//
// Lifecycle: This component is created with the
// 1. ideal value (component value based from computation, aka. textbook component in the old app, or ideal value),
// 2. a multiplier to get the minimum limit.
// 3. a multiplier to get the maximum limit.
//
// The ideal value is not directly used in here. It's only used to get the limits which are in turn used to compute
// a value based from c1's current value in comparison to its minimum and maximum (rcRatio).
//
// Construction: Capacitor.create({idealValue: idealValue, capacitorMaximumLimitMultiplier: capacitorMaximumLimitMultiplier, capacitorMinimumLimitMultiplier: capacitorMinimumLimitMultiplier})
// Hash:
// hash = {
//              idealValue: { number },
//              capacitorMaximumLimitMultiplier: { number },
//              capacitorMinimumLimitMultiplier: { number }
//        }
// Create: Capacitor.create(hash);
// @inputProperty { number } idealValue - The computed value based from a given c1.
// @inputProperty { number } capacitorMaximumLimitMultiplier - The capacitor's minimum limit multiplier
// @inputProperty { number } capacitorMinimumLimitMultiplier - The capacitor's maximum limit multiplier
// @inputProperty { number } rcRatio - The value of c1 relative to its minimum and maximum logarithmically.
// @inputProperty { FilterConstants.sizing } sizing - Indication whether the value of r1 is going towards its max or min.
// @getterProperty { number } nominalValue - The computed commercial value. This matches the value of capacitors that are available in the market.
// @getterProperty { number[] } toleranceArray - The nominal value multiplied to each of the elements of the array. The array consists of different tolerance multipliers.
// @getterProperty { FilterConstants.capacitorTolerance } finalTolerance - If the desired tolerance fails to get the nominal, there's a process that goes through all the available tolerance options to get the nominal value.
// @getterProperty { FilterConstants.capacitorTolerance } desiredTolerance - the selected capacitor tolerance or the default if none.
// @getterProperty { bool } isCommercial - True if this component is already converted into a commercial capacitor. False if not.
// @computed { number } maximum - the capacitor instance's maximum limit
// @computed { number } minimum - the capacitor instance's minimum limit
// @computed { number } sizingAdjustedValue - The computed ideal value based from the limits and the rcRatio
// @computed { number } value - the ideal value if the component is not commercial, the nominal value if the component is already converted to a commercial one.
// @method { this } toCommercialComponent() - converts the component into its commercial version.
// @method { number } getNominalValue() - gets the nominal value. Can only be accessed when the component is already a commercial one.
export default class Resistor extends EmberObject {
  idealValue = null;
  capacitorMaximumLimitMultiplier = null;
  capacitorMinimumLimitMultiplier = null;
  nominalValue = null;
  toleranceArray = null;
  finalTolerance = null;
  isCommercial = null;
  maximum = null;
  minimum = null;
  init() {
    super.init();
    set(this, 'isCommercial', false);

    this.maximum =
      CommonUtils.isNullOrUndefined(this.maximum) || this.maximum === 0
        ? CircuitUtils.computeResistorLimit(this.idealValue, this.capacitorMinimumLimitMultiplier)
        : this.maximum;
    this.minimum =
      CommonUtils.isNullOrUndefined(this.minimum) || this.minimum === 0
        ? CircuitUtils.computeResistorLimit(this.idealValue, this.capacitorMaximumLimitMultiplier)
        : this.minimum;
    this.value = CircuitUtils.computeComponentValueFromMinMaxAndRcRatio(this.minimum, this.maximum, this.rcRatio);
  }

  toCommercialComponent(toleranceInput) {
    set(this, 'toleranceInput', toleranceInput);
    let valueAdjusted = CircuitUtils.computeComponentValueFromMinMaxAndRcRatio(
      this.minimum,
      this.maximum,
      this.rcRatio,
    );
    let tolInput = !toleranceInput ? CircuitUtils.createToleranceInputDefaults() : toleranceInput;
    // let nominalValue = ResistorToleranceUtils.computeNominalValue(valueAdjusted, tolInput, 'closest');
    let nominalValue = computeNominalValue(valueAdjusted, tolInput.resistorPreferredSeries, 'closest');
    let toleranceValues = ResistorToleranceUtils.getToleranceValues(nominalValue, tolInput.resistorTolerance);

    set(this, 'value', nominalValue);
    set(this, 'toleranceArray', toleranceValues.toleranceArray);
    set(this, 'finalTolerance', toleranceValues.finalTolerance);
    set(this, 'desiredTolerance', tolInput.resistorTolerance);
    set(this, 'isCommercial', true);

    return this;
  }

  getNominalValue() {
    if (!this.isCommercial) throw 'component is not yet commercial. Call toCommercialComponent() first.';

    return this.value;
  }
}
